import type { PartnerContainer, PartnerContainerOption } from '../products/types';
import type { WorkOrderRow } from './types';

/**
 * Per-row applicability check for a container column. Returns true when the
 * cell should be editable / rendered as data.
 *
 * An undefined row (AG Grid group, pinned-bottom, or loading row) is treated
 * as applicable so the framework's internal rendering isn't blocked. A row
 * whose product_type cell is empty or doesn't resolve via the provided code
 * map also falls through as applicable: the grid shouldn't gate edits until
 * the user has picked a valid Product Type.
 */
export function isContainerApplicableForRow(
  row: WorkOrderRow | undefined,
  container: PartnerContainer,
  ptCodeToOptionId: ReadonlyMap<string, string>,
): boolean {
  if (!row) return true;
  if (container.container_key === 'product_type') return true;
  const code = row['product_type'];
  if (typeof code !== 'string' || code === '') return true;
  const ptOptionId = ptCodeToOptionId.get(code);
  if (!ptOptionId) return true;
  return container.product_type_option_ids.includes(ptOptionId);
}

/**
 * Build an option_code -> container_option_id map from the Product Type
 * container's options, used to resolve a row's product_type cell value (an
 * option_code) back to the id the allowlist checks against. Empty map when
 * the containers list doesn't include a product_type container.
 */
export function buildProductTypeCodeMap(
  containers: PartnerContainer[],
): ReadonlyMap<string, string> {
  const pt = containers.find((c) => c.container_key === 'product_type');
  if (!pt) return new Map();
  return new Map(pt.options.map((o) => [o.option_code, o.container_option_id]));
}

/**
 * ENG-677: whether a container OPTION is selectable (green) for a given
 * product-type option — i.e. the product type is in the option's resolved green
 * set. Fail-soft: an option missing the field (older cached data) is treated as
 * applicable so the dropdown never wrongly empties.
 */
export function optionAppliesToProductType(
  option: Pick<PartnerContainerOption, 'product_type_option_ids'>,
  productTypeOptionId: string,
): boolean {
  const ids = option.product_type_option_ids;
  if (!ids) return true;
  return ids.includes(productTypeOptionId);
}

/**
 * The subset of a container's options selectable for a given row, filtered by
 * the row's product type (ENG-677). The product_type container is never
 * option-scoped; and when the row has no resolvable product type we fail soft to
 * the full list, mirroring {@link isContainerApplicableForRow}. When the
 * container IS linked to the row's product type but every option excludes it
 * (the yellow dead state), this legitimately returns an empty list.
 */
export function optionsForRow(
  container: PartnerContainer,
  row: WorkOrderRow | undefined,
  ptCodeToOptionId: ReadonlyMap<string, string>,
): PartnerContainerOption[] {
  if (container.container_key === 'product_type') return container.options;
  const code = row?.['product_type'];
  if (typeof code !== 'string' || code === '') return container.options;
  const ptOptionId = ptCodeToOptionId.get(code);
  if (!ptOptionId) return container.options;
  return container.options.filter((option) => optionAppliesToProductType(option, ptOptionId));
}
